import {
  // KEY_PREFIX,
  AUTOMATION_KEY,
  BROWSERS,
  DATA_SAVE_MODE_KEY,
  NETWORK_KEY,
  ON_KEY,
  PLATFORM_KEY,
  REFERRER_KEY,
  DEVICE_TYPE
} from "./constants.ts";
import type { DeviceInfo } from "./types/types.ts";
import Cookie from "./Cookie.ts";

export type AnyElement = EventTarget & {
  addEventListener?: (...args: any[]) => void;
  attachEvent?: (...args: any[]) => void;
  removeEventListener?: (...args: any[]) => void;
  detachEvent?: (...args: any[]) => void;
  [key: string]: any; // Changed to any to match property indexing
};

export const isUndefined = (term: unknown): term is undefined =>
  typeof term === "undefined";

export const isNull = (term: unknown): term is null => term === null;

export const isDefined = <T>(term: T | null | undefined): term is T =>
  typeof term !== "undefined" && !isNull(term);

export const isNotDefined = (term: unknown): boolean => !isDefined(term);

export const isFunction = (
  fn: unknown
): fn is (...args: unknown[]) => unknown => typeof fn === "function";

export const isString = (term: unknown): term is string =>
  typeof term === "string";

export const isBoolean = (x: unknown): x is boolean => typeof x === "boolean";

export const isNumber = (term: unknown): term is number =>
  isDefined(term) && !isNull(term) && !isNaN(term as number);

export const executeIfDOMReady = (
  win: Window & typeof globalThis = window,
  callback: () => void
): void => {
  if (callback == null || !isFunction(callback)) return;
  const { document } = win;
  if (document.readyState !== "loading") {
    callback();
  } else if (isFunction(document.addEventListener)) {
    document.addEventListener("DOMContentLoaded", callback);
  } else if (
    isFunction(
      (win as unknown as { attachEvent?: (...a: unknown[]) => void })
        .attachEvent
    )
  ) {
    (
      win as unknown as {
        attachEvent: (e: string, fn: (e: Event) => void) => void;
      }
    ).attachEvent("onreadystatechange", function (e: Event) {
      if (document.readyState !== "loading") callback();
    });
  }
};

export const executeBeforeUnload = (
  win: Window = window,
  fn: (e: Event) => void = () => null
): void => {
  if (isFunction(fn)) {
    win.addEventListener("pagehide", (e) => {
      fn(e);
      return "";
    });
    win.addEventListener("beforeunload", (e) => {
      fn(e);
      return "";
    });
    win.addEventListener("onunload", (e) => {
      fn(e);
      return "";
    });
  }
};

export const unregisterBeforeUnloadCallback = (
  win: Window | Document = window,
  fn: (e: Event) => void = () => null
): void => {
  if (isFunction(fn)) {
    (win as Window).removeEventListener("pagehide", fn);
    (win as Window).removeEventListener("beforeunload", fn);
    (win as Window).removeEventListener("onunload", fn);
  }
};

export const registerForEvent = (
  element: AnyElement,
  eventType: string,
  handler: EventListenerOrEventListenerObject,
  passive = false
): void => {
  if (isFunction(element.addEventListener)) {
    (element as EventTarget).addEventListener(eventType, handler, passive);
  } else if (isFunction(element.attachEvent)) {
    (
      element as unknown as { attachEvent: (t: string, h: unknown) => void }
    ).attachEvent(eventType, handler);
  } else {
    element[ON_KEY + eventType] = handler;
  }
};

export const deregisterFromEvent = (
  element: AnyElement,
  eventType: string,
  handler: EventListenerOrEventListenerObject
): void => {
  if (isFunction(element.removeEventListener)) {
    (element as EventTarget).removeEventListener(eventType, handler);
  } else if (isFunction(element.detachEvent)) {
    (
      element as unknown as { detachEvent: (t: string, h: unknown) => void }
    ).detachEvent(eventType, handler);
  } else {
    element[ON_KEY + eventType] = handler;
  }
};

export const formatDatetime = (
  date: Date,
  withTimeZoneOffset = true
): string => {
  const withPadding = (n: number) => (n < 10 ? "0" + n : n);
  const utc =
    date.getFullYear() +
    "-" +
    withPadding(date.getMonth() + 1) +
    "-" +
    withPadding(date.getDate()) +
    "T" +
    withPadding(date.getHours()) +
    ":" +
    withPadding(date.getMinutes()) +
    ":" +
    withPadding(date.getSeconds());
  if (withTimeZoneOffset) {
    return (
      utc +
      date
        .toTimeString()
        .split(" ")[1]
        .replace(/GMT([+|-])(\d{2})(\d{2})/, "$1$2:$3")
    );
  }
  return utc;
};

export const getNow = (): number => Date.now();

export const uuid = function b(a: string | null = null): string {
  return a
    ? (parseInt(a) ^ ((Math.random() * 16) >> (parseInt(a) / 4))).toString(16)
    : (([1e7] as unknown as string) + -1e3 + -4e3 + -8e3 + -1e11).replace(
        /[018]/g,
        (c) => b(c)
      );
};

export const withTimeout = (
  callback: () => void,
  wait = 1
): ReturnType<typeof setTimeout> => {
  let called = false;
  const fn = () => {
    if (!called) {
      called = true;
      callback();
    }
  };
  return setTimeout(fn, wait);
};

const getDevice = (): string => {
  const { navigator: { userAgent = "" } = {} } = window;
  switch (true) {
    case /Windows Phone/i.test(userAgent) || /WPDesktop/.test(userAgent):
      return "Windows Phone";
    case /iPad/.test(userAgent):
      return "iPad";
    case /iPod/.test(userAgent):
      return "iPod Touch";
    case /iPhone/.test(userAgent):
      return "iPhone";
    case /(BlackBerry|PlayBook|BB10)/i.test(userAgent):
      return "BlackBerry";
    case /Android/.test(userAgent):
      return "Android";
    default:
      return "Desktop";
  }
};

export const getDeviceInfo = (win: Window = window): DeviceInfo => {
  const { navigator: { userAgent = "", vendor = "" } = {} } = win;

  const {
    OperaMini,
    Opera,
    BlackBerry,
    IEMobile,
    Edge,
    FB,
    Chrome,
    ChromeIOS,
    UC,
    FirefoxIOS,
    SafariMobile,
    Safari,
    AndroidMobile,
    Konqueror,
    Firefox,
    IE,
    Mozilla,
    Brave
  } = BROWSERS;

  const getBrowser = (): string => {
    if ((navigator as unknown as { brave?: unknown })?.brave) return Brave;
    switch (true) {
      case userAgent.includes(" OPR/"):
        return userAgent.includes("Mini") ? OperaMini : Opera;
      case /(BlackBerry|PlayBook|BB10)/i.test(userAgent):
        return BlackBerry;
      case userAgent.includes("IEMobile") || userAgent.includes("WPDesktop"):
        return IEMobile;
      case userAgent.includes("FBIOS"):
        return FB;
      case userAgent.includes("Chrome"):
        return Chrome;
      case userAgent.includes("CriOS"):
        return ChromeIOS;
      case userAgent.includes("Edge"):
        return Edge;
      case userAgent.includes("UCWEB") || userAgent.includes("UCBrowser"):
        return UC;
      case userAgent.includes("FxiOS"):
        return FirefoxIOS;
      case vendor.includes("Apple"):
        return userAgent.includes("Mobile") ? SafariMobile : Safari;
      case userAgent.includes("Android"):
        return AndroidMobile;
      case userAgent.includes("Konqueror"):
        return Konqueror;
      case userAgent.includes("Firefox"):
        return Firefox;
      case userAgent.includes("MSIE") || userAgent.includes("Trident/"):
        return IE;
      case userAgent.includes("Gecko"):
        return Mozilla;
      default:
        return "";
    }
  };

  const browser = getBrowser();

  const getBrowserVersion = (): number | null => {
    const versionRegExMap: Record<string, RegExp> = {
      [IEMobile]: /rv:(\d+(\.\d+)?)/,
      [Edge]: /Edge\/(\d+(\.\d+)?)/,
      [Chrome]: /Chrome\/(\d+(\.\d+)?)/,
      [ChromeIOS]: /CriOS\/(\d+(\.\d+)?)/,
      [UC]: /(UCBrowser|UCWEB)\/(\d+(\.\d+)?)/,
      [Safari]: /Version\/(\d+(\.\d+)?)/,
      [SafariMobile]: /Version\/(\d+(\.\d+)?)/,
      [Opera]: /(Opera|OPR)\/(\d+(\.\d+)?)/,
      [Firefox]: /Firefox\/(\d+(\.\d+)?)/,
      [FirefoxIOS]: /FxiOS\/(\d+(\.\d+)?)/,
      [Konqueror]: /Konqueror:(\d+(\.\d+)?)/,
      [BlackBerry]: /BlackBerry (\d+(\.\d+)?)/,
      [AndroidMobile]: /android\s(\d+(\.\d+)?)/,
      [IE]: /(rv:|MSIE )(\d+(\.\d+)?)/,
      [Mozilla]: /rv:(\d+(\.\d+)?)/
    };
    const versionRegEx = versionRegExMap[browser];
    if (!versionRegEx) return null;
    const matches = userAgent.match(versionRegEx);
    if (!matches) return null;
    return parseFloat(matches[matches.length - 2]);
  };

  const getOS = (): string => {
    switch (true) {
      case /Windows/i.test(userAgent):
        return /Phone/.test(userAgent) || /WPDesktop/.test(userAgent)
          ? "Windows Phone"
          : "Windows";
      case /(iPhone|iPad|iPod)/.test(userAgent):
        return "iOS";
      case /Android/.test(userAgent):
        return "Android";
      case /(BlackBerry|PlayBook|BB10)/i.test(userAgent):
        return "BlackBerry";
      case /Mac/i.test(userAgent):
        return "Mac OS X";
      case /Linux/.test(userAgent):
        return "Linux";
      case /CrOS/.test(userAgent):
        return "Chrome OS";
      default:
        return "";
    }
  };

  const getDimensions = () => {
    const {
      screen: { height, width } = { width: 0, height: 0 },
      devicePixelRatio = 1
    } = win;
    const roundedDimension = (dim: number) => {
      const mul = Math.round(dim) * devicePixelRatio;
      const mod = mul % 10;
      return mul + (mod > 0 ? 10 : 0) - mod;
    };
    return {
      dimensions: { width, height },
      dimensions_in_pixels: {
        width: roundedDimension(width),
        height: roundedDimension(height)
      },
      dpi: devicePixelRatio.toString()
    };
  };

  return {
    ...getDimensions(),
    hardware_model: getDevice(),
    $browser_version: getBrowserVersion(),
    $browser: browser,
    os_version: getOS()
  };
};

export const getDefaultUserProperties = (
  win: Window = window,
  deviceInfo: Partial<DeviceInfo> = {}
): Record<string, unknown> => {
  const KEY_PREFIX = "$";
  const { navigator: { language } = {} } = win;
  const { $browser_version, $browser } = deviceInfo;
  const cookie_supported = Cookie.isCookieAvailable();
  return {
    [KEY_PREFIX + "browser"]: $browser,
    [KEY_PREFIX + "browser_version"]: $browser + "_" + $browser_version,
    [KEY_PREFIX + "language"]: language,
    [KEY_PREFIX + "cookies_supported"]: cookie_supported
  };
};

export const getDefaultSessionProperties = (
  win: Window = window
): Record<string, unknown> => {
  const {
    navigator: { connection, platform, webdriver } = {} as Navigator & {
      connection?: { effectiveType?: string; saveData?: boolean };
    },
    document: { referrer }
  } = win as Window & {
    navigator: Navigator & {
      connection?: { effectiveType?: string; saveData?: boolean };
    };
  };

  const properties: Record<string, unknown> = {};
  const effectiveType = (connection as { effectiveType?: string } | undefined)
    ?.effectiveType;
  const KEY_PREFIX = "$";
  const saveData = (connection as { saveData?: boolean } | undefined)?.saveData;

  if (effectiveType) properties[KEY_PREFIX + NETWORK_KEY] = effectiveType;
  if (saveData) properties[KEY_PREFIX + DATA_SAVE_MODE_KEY] = saveData;
  if (platform) properties[KEY_PREFIX + PLATFORM_KEY] = platform;
  if (isDefined(webdriver) && webdriver)
    properties[KEY_PREFIX + AUTOMATION_KEY] = webdriver;
  if (isDefined(referrer) && referrer.length > 0) {
    properties[KEY_PREFIX + REFERRER_KEY] = referrer;
  } else {
    properties[KEY_PREFIX + REFERRER_KEY] = "direct";
  }
  properties[KEY_PREFIX + DEVICE_TYPE] = getDevice();
  return properties;
};

export const isTrackingEnabled = (win: Window = window): boolean => {
  if (win.navigator) {
    const dnt = (win.navigator as Navigator & { doNotTrack?: string })
      .doNotTrack;
    return !(dnt === "1" || dnt === "yes");
  }
  return true;
};

export const getPageTitle = (win: Window): string => {
  const node = win.document.querySelector("title");
  if (!isDefined(node) || !isDefined(node.innerText)) return "Unknown";
  return node.innerText;
};

export const millisToSeconds = (millis: number): number =>
  isNumber(millis) ? toDecimalPoints(millis / 1000) : millis;

export const toDecimalPoints = (number: number, decimalPlaces = 3): number => {
  if (!isNumber(number) || !isNumber(decimalPlaces)) return number;
  if (decimalPlaces % 1 !== 0) return number;
  const power = Math.pow(10, decimalPlaces);
  return Math.round(number * power) / power;
};

const FILE_PROTOCOL = "file:";
const LOCALHOST = "localhost";
const APP_MODE_PRODUCTION = "production";
const APP_MODE_DEV = "development";

export const getAppMode = (win: Window = window): string => {
  const { location: { protocol, host, hostname } = {} as Location } = win;
  if (
    protocol === FILE_PROTOCOL ||
    host === LOCALHOST ||
    hostname === "0.0.0.0" ||
    hostname === "127.0.0.1"
  ) {
    return APP_MODE_DEV;
  }
  return APP_MODE_PRODUCTION;
};

export const isObject = (obj: unknown): obj is object => obj instanceof Object;

export const getCleanedProperties = (
  obj: Record<string, unknown> = {},
  depth = 1
): Record<string, unknown> => {
  if (isObject(obj)) {
    const cleanedJson: Record<string, unknown> = {};
    Object.keys({ ...obj }).forEach((key) => {
      const value = obj[key];
      let propertyKey = key;
      if (!isString(key)) propertyKey = JSON.stringify(key);

      if (isBoolean(value) || isNumber(value)) {
        insertProperty(cleanedJson, propertyKey, value);
      } else if (isString(value)) {
        cleanedJson[propertyKey] = trimText(value);
      } else if (Array.isArray(value)) {
        handleJSONArray(cleanedJson, propertyKey, value, depth);
      } else if (isObject(value)) {
        handleJSONObject(
          cleanedJson,
          key,
          value as Record<string, unknown>,
          depth - 1
        );
      }
    });
    return cleanedJson;
  }
  return {};
};

const handleJSONArray = (
  object: Record<string, unknown>,
  key: string,
  value: unknown[],
  depth = 1
): void => {
  if (depth <= 0) {
    insertProperty(object, key, JSON.stringify(value));
    return;
  }
  if (Array.isArray(value)) {
    const jsonArray = value;
    if (jsonArray.length > 0) {
      let type = "";
      if (isBoolean(jsonArray[0])) type = "b";
      else if (isString(jsonArray[0])) type = "s";
      else if (isNumber(jsonArray[0])) type = "n";
      else if (Array.isArray(jsonArray[0])) type = "a";
      else if (isObject(jsonArray[0])) type = "o";

      const length = jsonArray.length;
      if (type === "") {
        insertProperty(object, key, JSON.stringify(value));
        return;
      }

      for (let index = 1; index < length; index++) {
        if (
          (type === "s" && !isString(jsonArray[index])) ||
          (type === "b" && !isBoolean(jsonArray[index])) ||
          (type === "n" && !isNumber(jsonArray[index])) ||
          (type === "a" && !Array.isArray(jsonArray[index])) ||
          (type === "o" && !isObject(jsonArray[index]))
        ) {
          insertProperty(object, key, JSON.stringify(value));
          return;
        }
      }

      if (type === "o") {
        if (depth - 1 <= 0) {
          const array: string[] = [];
          for (let index = 0; index < length; index++) {
            array.push(JSON.stringify(jsonArray[index]));
          }
          insertProperty(object, key, array);
        } else {
          for (let index = 0; index < length; index++) {
            handleJSONObject(
              object,
              key,
              jsonArray[index] as Record<string, unknown>,
              depth - 1
            );
          }
        }
      } else if (type === "a") {
        for (let index = 0; index < length; index++) {
          insertProperty(object, key, JSON.stringify(jsonArray[index]));
        }
      } else {
        for (let index = 0; index < length; index++) {
          insertProperty(object, key, jsonArray[index]);
        }
      }
    } else {
      insertProperty(object, key, []);
    }
  }
};

const handleJSONObject = (
  object: Record<string, unknown>,
  key: string,
  jsonObject: Record<string, unknown>,
  depth = 1
): void => {
  if (depth <= 0) {
    insertProperty(object, key, JSON.stringify(jsonObject));
    return;
  }
  Object.keys({ ...jsonObject }).forEach((objectKey) => {
    const updatedKey = key + "_" + objectKey;
    const value = jsonObject[objectKey];
    if (!isDefined(value)) return;
    if (Array.isArray(value)) {
      handleJSONArray(object, updatedKey, value, depth - 1);
    } else if (isObject(value)) {
      handleJSONObject(
        object,
        updatedKey,
        value as Record<string, unknown>,
        depth - 1
      );
    } else {
      insertProperty(object, updatedKey, value);
    }
  });
};

const insertProperty = (
  object: Record<string, unknown>,
  key: string,
  value: unknown
): void => {
  if (!isDefined(key) || !isDefined(value)) return;
  if (object[key] !== undefined) {
    let values = object[key];
    let array: unknown[];
    if (!Array.isArray(values)) {
      if (
        (isNumber(values) && !isNumber(value)) ||
        (isBoolean(values) && !isBoolean(value)) ||
        (isString(values) && !isString(value)) ||
        (isObject(values) && !isObject(value))
      ) {
        if (!isString(values)) values = JSON.stringify(values);
        if (!isString(value)) value = JSON.stringify(value);
      }
      array = [values];
    } else {
      array = values;
    }
    array.push(value);
    object[key] = array;
  } else {
    object[key] = value;
  }
};

export const trimText = (text: unknown, limit = 120): string | undefined => {
  if (isString(text)) {
    return text.length > limit ? text.slice(0, limit) + "..." : text;
  }
  return undefined;
};

export const getJSONOrNull = (text: unknown): unknown => {
  if (!isDefined(text) && !isString(text)) return null;
  if (typeof text === "object" || isObject(text)) return text;
  const newText = (text as string).replace(/(?:\r\n|\r|\n)/g, "").trim();
  try {
    return JSON.parse(newText);
  } catch (e) {
    /* empty */
  }
  return null;
};

export const encode = (
  key: string,
  data: string | null | undefined
): string | null => {
  if (!isDefined(data)) return null;
  const encrypted = xor_encrypt(key, data!);
  return b64_encode(encrypted);
};

export const decode = (key: string, data: string | number[]): string => {
  const decoded = b64_decode(data as string);
  return xor_decrypt(key, decoded);
};

const b64_table =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=";

function b64_encode(data: number[]): string {
  if (!data) return data as unknown as string;
  let enc = "";
  let i = 0;
  do {
    const o1 = data[i++];
    const o2 = data[i++];
    const o3 = data[i++];
    const bits = (o1 << 16) | (o2 << 8) | o3;
    const h1 = (bits >> 18) & 0x3f;
    const h2 = (bits >> 12) & 0x3f;
    const h3 = (bits >> 6) & 0x3f;
    const h4 = bits & 0x3f;
    enc +=
      b64_table.charAt(h1) +
      b64_table.charAt(h2) +
      b64_table.charAt(h3) +
      b64_table.charAt(h4);
  } while (i < data.length);
  const r = data.length % 3;
  return (r ? enc.slice(0, r - 3) : enc) + "===".slice(r || 3);
}

function b64_decode(data: string): number[] {
  if (!data) return data as unknown as number[];
  const result: number[] = [];
  let i = 0;
  const s = data + "";
  do {
    const h1 = b64_table.indexOf(s.charAt(i++));
    const h2 = b64_table.indexOf(s.charAt(i++));
    const h3 = b64_table.indexOf(s.charAt(i++));
    const h4 = b64_table.indexOf(s.charAt(i++));
    const bits = (h1 << 18) | (h2 << 12) | (h3 << 6) | h4;
    const o1 = (bits >> 16) & 0xff;
    const o2 = (bits >> 8) & 0xff;
    const o3 = bits & 0xff;
    result.push(o1);
    if (h3 !== 64) {
      result.push(o2);
      if (h4 !== 64) result.push(o3);
    }
  } while (i < s.length);
  return result;
}

function keyCharAt(key: string, i: number): number {
  return key.charCodeAt(Math.floor(i % key.length));
}

function xor_encrypt(key: string, data: string): number[] {
  return Array.from(data).map((c, i) => c.charCodeAt(0) ^ keyCharAt(key, i));
}

function xor_decrypt(key: string, data: number[]): string {
  return Array.from(data)
    .map((c, i) => String.fromCharCode(c ^ keyCharAt(key, i)))
    .join("");
}

export function stringToArrayBuffer(str: string): ArrayBuffer {
  const buffer = new ArrayBuffer(str.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < str.length; i++) {
    view[i] = str.charCodeAt(i);
  }
  return buffer;
}

export function arrayBufferToString(buffer: ArrayBufferLike): string {
  let str = "";
  const array = new Uint8Array(buffer);
  for (let i = 0; i < array.length; i++) {
    str += String.fromCharCode(array[i]);
  }
  return str;
}

export const getDateInDDMMYYYY = (): string => {
  const currentDate = new Date();
  return `${currentDate.getDate()}/${currentDate.getMonth()}/${currentDate.getFullYear()}`;
};

export const getFlatMapDiff = (
  updatedProps: Record<string, unknown>,
  originalProps: Record<string, unknown>
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  Object.keys(updatedProps).forEach((key) => {
    if (updatedProps[key] !== originalProps[key]) {
      result[key] = updatedProps[key];
    }
  });
  return result;
};

export const getDateInMMDDYYYY = (): string => {
  const currentDate = new Date();
  return `${currentDate.getMonth()}/${currentDate.getDate()}/${currentDate.getFullYear()}`;
};

export const _getTime = (): { hours: number; mins: number } => {
  const currentTime = new Date();
  return { hours: currentTime.getHours(), mins: currentTime.getMinutes() };
};

export const ensureSessionInfoKeys = (chunk: {
  session_info?: Record<string, unknown>;
}): void => {
  const defaultSessionInfo: Record<string, unknown> = {
    id: "",
    launch_time: 0,
    launch_time_string: "",
    startup_time: 0,
    is_background: false,
    is_first_session: false,
    duration: 0,
    total_duration: 0,
    process_name: "",
    is_fatal: false,
    launch_type: "normal"
  };
  if (!chunk.session_info) {
    chunk.session_info = defaultSessionInfo;
  } else {
    for (const key of Object.keys(defaultSessionInfo)) {
      if (!(key in chunk.session_info)) {
        chunk.session_info[key] = defaultSessionInfo[key];
      }
    }
  }
};

export const ignoreClientEventProps = (
  name: string,
  additional_info: Record<string, unknown>,
  ignoreAllEventProps: string[],
  ignoreSpecificEventProps: Record<string, string[]>
): Record<string, unknown> => {
  if (ignoreAllEventProps && additional_info) {
    ignoreAllEventProps.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(additional_info, key)) {
        delete additional_info[key];
      }
    });
  }
  if (!ignoreSpecificEventProps || !additional_info) return additional_info;
  if (ignoreSpecificEventProps[name]) {
    ignoreSpecificEventProps[name].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(additional_info, key)) {
        delete additional_info[key];
      }
    });
  }
  return additional_info;
};

export const ignoreUserProps = (
  propsToIgnore: string[] | undefined,
  userProps: Record<string, unknown>
): Record<string, unknown> => {
  if (isObject(userProps) && propsToIgnore) {
    propsToIgnore.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(userProps, key)) {
        delete userProps[key];
      }
    });
  }
  return userProps;
};

export const ignoreSessionProps = (
  propsToIgnore: string[] | undefined,
  sessionProps: Record<string, unknown>
): Record<string, unknown> => {
  if (isObject(sessionProps) && propsToIgnore) {
    propsToIgnore.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(sessionProps, key)) {
        delete sessionProps[key];
      }
    });
  }
  return sessionProps;
};
